import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faUser, faRobot, faCommentDots, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import './NutritionChatWidget.css';

const NutritionChatWidget = ({ appContext }) => {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const messagesEndRef = useRef(null);

    const suggestedQuestions = [
        "How do I use the Food Scanner?",
        "Can you help me create a meal plan?",
        "What are some healthy snack ideas?",
        "Explain my current calorie target."
    ];

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim()) return;
        const userMessage = { role: 'user', content: input.trim() };
        const newMessages = [...messages, userMessage];

        setMessages(newMessages);
        setInput('');
        setLoading(true);
        if (showSuggestions) {
            setShowSuggestions(false);
        }

        let contextString = "The user is interacting with the MyFit Menu AI application.";
        if (appContext) {
            if (appContext.currentSection) {
                contextString += ` They are currently in the "${appContext.currentSection}" section.`;
            }
            if (appContext.userProfileSummary) {
                contextString += `\nUser Profile Context: Primary goal is ${appContext.userProfileSummary.primaryGoal || 'not set'}. Restrictions: ${appContext.userProfileSummary.restrictions || 'none specified'}.`;
            }
            if (appContext.lastScannedItem) {
                const item = appContext.lastScannedItem;
                if (item && item.rating && item.ratingExplanation) {
                    contextString += `\nThey might be referring to a recently analyzed food item:\n- Health Rating: ${item.rating}/10 (${item.ratingExplanation})\n- Concerning Ingredients: ${item.ingredients?.concerning?.map(i => i.name).join(', ') || 'N/A'}\n- Beneficial Ingredients: ${item.ingredients?.safe?.join(', ') || 'N/A'}\n- Insights: ${item.insights?.map(i => i.category).join(', ') || 'N/A'}`;
                }
            }
            if (appContext.lastGeneratedMenu) {
                contextString += "\nA weekly menu was recently generated. They might ask questions about it.";
            }
        }

        const systemPromptContent = `You are MyFit Menu AI, an AI assistant SOLELY for the MyFit Menu application.
Your PRIMARY and ONLY function is to assist users with nutrition, health, fitness, and the features of THIS APP.
STRICTLY AVOID answering questions outside of these topics.
If a user asks an off-topic question, politely decline and gently redirect them back to the app's purpose. For example: "My apologies, but I'm here to help you with nutrition and fitness within the MyFit Menu app. Do you have any questions about your diet, workouts, or how to use the app's features?"

Your capabilities include:
- Providing detailed nutritional information, explaining health implications of foods and ingredients.
- Helping users understand and navigate the app's features (e.g., Food Scanner, Recipe Generator, Nutrition Plan, Workout Plan).
- Assisting with generating meal plans or workout routines by providing example outputs or guiding them on how to use the app's features. You cannot directly trigger app functions.
- Answering general health, fitness, and dietary questions.
- Adapting your responses based on the user's profile (like goals and restrictions if provided in context).
- Always respond in the language of the user's last query. If the user asks in Spanish, respond in Spanish.

Current application context for your awareness (use this information to tailor your response if relevant to the user's query):
${contextString}

Engage naturally but stay focused on your role.
Refer to previous turns in THIS conversation if they provide relevant context for the current query.
Always focus your main answer on the user's *most recent* health/fitness/app-related question.
Be helpful, accurate, concise, and evidence-based.
If you don't know the answer, say "I don't know" or "I'm not sure" instead of making up information.

**Formatting Instructions:**
- Use Markdown for formatting when it improves clarity.
- For lists, use numbered lists (e.g., 1., 2., 3.) or bullet points (e.g., * item or - item).
- Use bold text (**text**) for emphasis on key terms or headings within your response.
- Use line breaks (by generating actual newlines in your response, which will be treated as <br> or new paragraphs by Markdown) appropriately to separate paragraphs or steps in a list for better readability.
- For example, if you provide steps:
1. **First Step:** Explanation.
2. **Second Step:** More details.
- If you list ingredients:
  * Ingredient A
  * Ingredient B
- Do not use HTML tags in your response.
`;

        const systemMessage = {
            role: 'system',
            content: systemPromptContent
        };

        const MAX_HISTORY_MESSAGES = 10;
        const truncatedMessages = newMessages.slice(-MAX_HISTORY_MESSAGES);

        try {
            const response = await fetch('https://text.pollinations.ai/v1/chat/completions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    model: 'openai-large',
                    messages: [systemMessage, ...truncatedMessages],
                    temperature: 0.6,
                    max_tokens: 1200,
                    private: true
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: "Unknown API error from server" }));
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorData.detail || errorData.message || JSON.stringify(errorData)}`);
            }

            const data = await response.json();
            const aiResponse = data.choices && data.choices[0]?.message?.content
                ? data.choices[0].message.content.trim()
                : "Sorry, I couldn't generate a response at this moment. Please try again.";

            const aiMessage = { role: 'assistant', content: aiResponse };
            setMessages([...newMessages, aiMessage]);

        } catch (error) {
            console.error('Error in AI response:', error);
            setMessages([...newMessages, { role: 'assistant', content: `Sorry, something went wrong. (${error.message})` }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`nutrition-chat-widget ${open ? 'open' : ''}`}>
            <span className="chat-tooltip">Ask MyFit Menu AI Chatbot</span>

            <div className="chat-header" onClick={() => setOpen(!open)}>
                <FontAwesomeIcon icon={faCommentDots} className="chat-header-icon" />
                <h3>Ask MyFit Menu AI Chatbot</h3>
                <FontAwesomeIcon icon={open ? faTimes : faRobot} className="chat-action-icon" />
            </div>

            {open && (
                <div className="chat-container">
                    <div className="messages">
                        {messages.map((msg, index) => (
                            <div key={index} className={`message ${msg.role}`}>
                                {msg.role === 'assistant' && (
                                    <div className="message-icon">
                                        <FontAwesomeIcon icon={faRobot} />
                                    </div>
                                )}
                                <div className="message-text-bubble">
                                    {msg.role === 'user' ? (
                                        <p>{msg.content}</p>
                                    ) : (
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && (<div className="loading"></div>)}
                        <div ref={messagesEndRef} />
                    </div>

                    {showSuggestions && messages.length === 0 && (
                        <div className="suggested-questions">
                            {suggestedQuestions.map((question, index) => (
                                <span
                                    key={index}
                                    className="suggestion"
                                    onClick={() => {
                                        setInput(question);
                                        setShowSuggestions(false);
                                    }}
                                >
                                    {question}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="input-container">
                        <input
                            type="text"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !loading && input.trim()) { handleSend(); e.preventDefault(); } }}
                            placeholder="Ask something..."
                            disabled={loading}
                        />
                        <button onClick={handleSend} disabled={loading || !input.trim()}>
                            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NutritionChatWidget;